import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, writeBatch, updateDoc } from 'firebase/firestore';
import { nanoid } from 'nanoid';
import { Link } from 'react-router-dom';

const AdminCompaniesPage = () => {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch companies and add a temporary state for editing car slots
    const fetchCompanies = useCallback(async () => {
        setLoading(true);
        const q = query(collection(db, 'users'), where('role', '==', 'company'));
        const querySnapshot = await getDocs(q);
        const companiesData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            editableSlots: doc.data().carSlots || 0 // State for the input field
        }));
        setCompanies(companiesData);
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    // Handle changes to the car slot input field for a specific company
    const handleSlotChange = (userId, value) => {
        const newCompanies = companies.map(c => {
            if (c.id === userId) {
                return { ...c, editableSlots: Number(value) };
            }
            return c;
        });
        setCompanies(newCompanies);
    };

    // --- NEW: Function to update slots for an existing company ---
    const handleUpdateSlots = async (userId) => {
        const company = companies.find(c => c.id === userId);
        const newSlotCount = company.editableSlots;
        const currentSlotCount = company.carSlots;

        if (newSlotCount < currentSlotCount) {
            if (!window.confirm("Araç limitini düşürmek, mevcut QR kodlarını silmez ancak yeni atama yapılamaz. Emin misiniz?")) return;
        }

        const userDocRef = doc(db, 'users', userId);
        await updateDoc(userDocRef, { carSlots: newSlotCount });

        // If we are ADDING new slots, generate new QR codes for them
        if (newSlotCount > currentSlotCount) {
            const batch = writeBatch(db);
            const slotsToAdd = newSlotCount - currentSlotCount;
            for (let i = 0; i < slotsToAdd; i++) {
                const qrId = `RC-${nanoid(6).toUpperCase()}`;
                const qrDocRef = doc(db, 'qrCodes', qrId);
                const profileDocRef = doc(db, 'vehicleProfiles', qrId);
                
                // Add new QR code and vehicle profile to the batch
                batch.set(qrDocRef, { code: qrId, isLinked: true, linkedUserId: userId, companyId: company.companyProfileId, createdAt: new Date() });
                batch.set(profileDocRef, { userId: userId, companyId: company.companyProfileId, companyName: company.displayName, vehiclePlate: '', isRented: false, renterInfo: {}, availabilityInfo: {}, createdAt: new Date() });
            }
            await batch.commit();
            alert(`${slotsToAdd} adet yeni araç slotu ve QR kodu oluşturuldu.`);
        }
        
        fetchCompanies(); // Refresh data from Firestore
    };

    // --- UPDATED: Approval function now reads from the input field ---
    const handleApprove = async (userId) => {
        const company = companies.find(c => c.id === userId);
        const slotsToApprove = company.editableSlots;
        if (slotsToApprove <= 0) {
            alert("Onaylamak için lütfen en az 1 araç limiti girin.");
            return;
        }

        const userDocRef = doc(db, 'users', userId);
        const companyProfileRef = doc(db, 'companies', company.companyProfileId);
        const batch = writeBatch(db);

        // Update user and company documents
        batch.update(userDocRef, { isApproved: true, carSlots: slotsToApprove });
        batch.update(companyProfileRef, { isActive: true });
        
        // Generate initial QR codes
        // (This logic is the same as in handleUpdateSlots)
        for (let i = 0; i < slotsToApprove; i++) {
            const qrId = `RC-${nanoid(6).toUpperCase()}`;
            const qrDocRef = doc(db, 'qrCodes', qrId);
            const profileDocRef = doc(db, 'vehicleProfiles', qrId);
            batch.set(qrDocRef, { code: qrId, isLinked: true, linkedUserId: userId, companyId: company.companyProfileId, createdAt: new Date() });
            batch.set(profileDocRef, { userId: userId, companyId: company.companyProfileId, companyName: company.displayName, vehiclePlate: '', isRented: false, renterInfo: {}, availabilityInfo: {}, createdAt: new Date() });
        }

        await batch.commit();
        alert(`Şirket onaylandı ve ${slotsToApprove} adet araç slotu oluşturuldu.`);
        fetchCompanies();
    };


    if (loading) return <div className="text-center p-10">Şirketler Yükleniyor...</div>;

    return (
        <div className="container mx-auto p-8">
            <h1 className="text-3xl font-bold mb-6">Şirket Yönetimi</h1>
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <table className="min-w-full leading-normal">
                    <thead>
                        <tr>
                            <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Şirket Adı</th>
                            <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Durum</th>
                            <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Araç Limiti</th>
                            <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {companies.map(company => (
                            <tr key={company.id}>
                                <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                                    <p className="text-gray-900 whitespace-no-wrap font-semibold">{company.displayName}</p>
                                    <p className="text-gray-600 whitespace-no-wrap text-xs">{company.email}</p>
                                </td>
                                <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                                    {company.isApproved ? 
                                        <span className="relative inline-block px-3 py-1 font-semibold text-green-900 leading-tight">
                                            <span aria-hidden className="absolute inset-0 bg-green-200 opacity-50 rounded-full"></span>
                                            <span className="relative">Onaylı</span>
                                        </span> : 
                                        <span className="relative inline-block px-3 py-1 font-semibold text-orange-900 leading-tight">
                                            <span aria-hidden className="absolute inset-0 bg-orange-200 opacity-50 rounded-full"></span>
                                            <span className="relative">Onay Bekliyor</span>
                                        </span>
                                    }
                                </td>
                                <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                                    <input 
                                        type="number" 
                                        className="w-20 p-1 border rounded"
                                        value={company.editableSlots}
                                        onChange={(e) => handleSlotChange(company.id, e.target.value)}
                                        min="0"
                                    />
                                </td>
                                <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm space-x-2">
                                    {company.isApproved ? (
                                        <>
                                            <button onClick={() => handleUpdateSlots(company.id)} className="text-xs bg-blue-500 hover:bg-blue-700 text-white py-1 px-3 rounded">Limiti Güncelle</button>
                                            <Link to={`/admin/edit-company/${company.id}`} className="text-xs bg-gray-600 hover:bg-gray-800 text-white py-1 px-3 rounded">Detay</Link>
                                        </>
                                    ) : (
                                        <button onClick={() => handleApprove(company.id)} className="text-xs bg-green-500 hover:bg-green-700 text-white py-1 px-3 rounded">Onayla</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminCompaniesPage;